// rate-limit.service.ts (reemplaza/añade estas partes)
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { auditTime } from 'rxjs/operators';

export interface RateLimitState {
  isActive: boolean;
  timeRemaining: number;
  isHome: boolean;
}

export interface Raw429Event {
  retryAfterSeconds: number;
  blockId?: string | null;
  endpoint?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RateLimitService {
  private rateLimitStateSubject = new BehaviorSubject<RateLimitState>({
    isActive: false,
    timeRemaining: 0,
    isHome: false
  });
  public rateLimitState$: Observable<RateLimitState> = this.rateLimitStateSubject.asObservable();

  private countdownInterval: any = null;
  private readonly MAX_PENALTY_SECONDS = 600; // 10 min máx.
  private _isRedirecting = false;

  // Dedupe internals
  private lastBlockId: string | null = null;
  private lastActivationAt = 0; // ms
  private readonly COALESCE_MS = 10000; // 10s: si llegan 429 seguidos, tratarlos como 1 sólo evento (aumentado de 5s)

  // Control de ventana de suma: solo sumar penalizaciones dentro de una ventana corta
  private lastPenaltyAddedAt = 0; // timestamp de la última suma
  private readonly ACCUMULATION_WINDOW_MS = 2000; // 2 segundos: ventana para sumar penalizaciones

  // Subject para agrupar múltiples 429 y procesarlas una vez (auditTime)
  private raw429Subject = new Subject<Raw429Event>();
  private readonly GROUP_MS = 1000; // agrupa 1s para bursts (aumentado de 300ms para mejor agrupación)


// marca que hay un bloqueo "pending" establecido inmediatamente
private pendingBlock = false;
// info pendiente opcional
private pendingInfo: Raw429Event | null = null;

  constructor(private router: Router) {
    // Agrupador: cada GROUP_MS consolida eventos y procesa una sola activación
    this.raw429Subject.pipe(
      auditTime(this.GROUP_MS)
    ).subscribe((lastEvent: any) => {
      if (!lastEvent) { return; }
      // procesar el último evento del grupo
      this.processGrouped429(lastEvent);
    });
  }

  /**
   * Método público que el interceptor llama.
   * No activa inmediatamente si detecta duplicates; en vez, lo manda al subject para agrupar.
   */
  handle429(event: Raw429Event) {
    // Push al subject; auditTime agrupará múltiples pushes en GROUP_MS
    this.raw429Subject.next(event);
  }


/**
 * Compatibilidad pública: entra por aquí cualquier llamada externa (componentes, interceptor, etc.)
 * En lugar de activar inmediatamente, delega al pipeline de agrupación para evitar duplicados.
 *
 * Firma: activateRateLimit(retryAfterSeconds: number, isHome: boolean, blockId?: string | null, endpoint?: string)
 */
public activateRateLimit(retryAfterSeconds: number, isHome: boolean, blockId?: string | null, endpoint?: string): void {
  // Normalizar/restringir el valor de retry
  const limitedTime = Math.min(Math.max(Math.floor(retryAfterSeconds || 0), 0), this.MAX_PENALTY_SECONDS);

  // 1) Si ya hay un bloqueo activo, SUMAR el nuevo tiempo SOLO si está dentro de la ventana de acumulación
  //    Esto permite acumular múltiples penalizaciones de solicitudes en paralelo,
  //    pero evita sumar penalizaciones de acciones separadas del usuario
  const currentState = this.rateLimitStateSubject.value;
  if (currentState.isActive) {
    const nowMs = Date.now();
    const timeSinceLastPenalty = nowMs - this.lastPenaltyAddedAt;

    // Solo sumar si está dentro de la ventana de acumulación (ej: 2 segundos)
    if (timeSinceLastPenalty < this.ACCUMULATION_WINDOW_MS) {
      // console.log('[RateLimitService] activateRateLimit: dentro de ventana de acumulación. Sumando penalización...');

      // SUMAR el nuevo tiempo al tiempo restante actual
      const newTimeRemaining = Math.min(
        currentState.timeRemaining + limitedTime,
        this.MAX_PENALTY_SECONDS
      );

      // console.log('[RateLimitService] activateRateLimit: sumando', limitedTime, 's al tiempo actual', currentState.timeRemaining, 's. Nuevo total:', newTimeRemaining, 's');

      this.rateLimitStateSubject.next({
        ...currentState,
        timeRemaining: newTimeRemaining
      });

      this.lastPenaltyAddedAt = nowMs; // Actualizar timestamp
    } else {
      // console.log('[RateLimitService] activateRateLimit: fuera de ventana de acumulación (', timeSinceLastPenalty, 'ms). NO sumando. Manteniendo:', currentState.timeRemaining, 's');
    }

    if (blockId) {
      this.lastBlockId = blockId;
    }

    return; // Ya procesamos, salimos
  }

  // 2) Si no hay bloqueo activo, en lugar de disparar la activación inmediata,
  //    encolamos el evento para que pase por el mismo agrupador (auditTime + coalesce)
  //    y así todas las invocaciones (componentes + interceptor) se traten igual.
  // console.log('[RateLimitService] activateRateLimit: encolando evento de rate-limit (retry:', limitedTime, 's, blockId:', blockId, 'endpoint:', endpoint, ')');

  this.raw429Subject.next({
    retryAfterSeconds: limitedTime,
    blockId: blockId ?? null,
    endpoint: endpoint ?? undefined
  });

  // Nota: processGrouped429 será quien finalmente determine si activa el bloqueo real
  // (con coalescing por ventana y dedupe por blockId).
}



private processGrouped429(event: Raw429Event) {
  const nowMs = Date.now();
  const retryAfter = Math.min(event.retryAfterSeconds , this.MAX_PENALTY_SECONDS);

  // Si ya hay un bloqueo activo pero NO está en pending (es decir, ya fue activado),
  // SUMAR el nuevo tiempo SOLO si está dentro de la ventana de acumulación
  const currentState = this.rateLimitStateSubject.value;
  if (currentState.isActive && !this.pendingBlock) {
    const timeSinceLastPenalty = nowMs - this.lastPenaltyAddedAt;

    // Solo sumar si está dentro de la ventana de acumulación
    if (timeSinceLastPenalty < this.ACCUMULATION_WINDOW_MS) {
      const newTimeRemaining = Math.min(
        currentState.timeRemaining + retryAfter,
        this.MAX_PENALTY_SECONDS
      );

      // console.log('[RateLimitService] processGrouped429: sumando', retryAfter, 's al tiempo actual', currentState.timeRemaining, 's. Nuevo total:', newTimeRemaining, 's');

      this.rateLimitStateSubject.next({
        ...currentState,
        timeRemaining: newTimeRemaining
      });

      this.lastPenaltyAddedAt = nowMs;
    } else {
      // console.log('[RateLimitService] processGrouped429: fuera de ventana de acumulación. NO sumando.');
    }

    return;
  }

  // Si el bloqueo está en pending y el evento agrupado corresponde a ese pending (o es nuevo),
  // entonces procesamos la activación real Y limpiamos pending
  if (this.pendingBlock) {
    // Si el blockId coincide con el pending (y no es null), ok; si no coincide, aceptamos también
    if (this.pendingInfo && event.blockId && this.pendingInfo.blockId && event.blockId !== this.pendingInfo.blockId) {
      // console.log('[RateLimitService] processGrouped429: pending blockId difiere del evento agrupado, procediendo de todas formas.');
    }

    // Limpieza del pending — ahora vamos a hacer la activación definitiva (redirigir + contador)
    this.pendingBlock = false;
    this.pendingInfo = null;

    // Actualizar lastActivation/lastBlockId
    this.lastActivationAt = nowMs;
    this.lastBlockId = event.blockId ?? null;

    // console.log('[RateLimitService] 🚨 processGrouped429: activando bloqueo real desde pending. retry=', retryAfter);

    // Aquí mostramos notificación UI y activamos redirección/contador
    // (Si quieres mostrar UI justo antes de redirigir, hazlo aquí).
    this.activateRateLimitInternal(retryAfter, false);
    return;
  }

  // Si no había pending, aplicamos la coalescencia normal:
  // 1. Ignorar si es el mismo blockId que ya manejamos
  if (event.blockId && this.lastBlockId === event.blockId) {
    // console.log('[RateLimitService] Ignorado - mismo blockId.');
    return;
  }

  // 2. Ignorar si estamos dentro de la ventana de coalescencia
  if (nowMs - this.lastActivationAt < this.COALESCE_MS) {
    // console.log('[RateLimitService] Ignorado - dentro de ventana de coalescencia.');
    return;
  }

  // 3. Activación REAL (sin pending)
  this.lastActivationAt = nowMs;
  this.lastBlockId = event.blockId ?? null;

  // console.log('[RateLimitService] 🚨 processGrouped429: activando bloqueo real (sin pending). retry=', retryAfter);

  this.activateRateLimitInternal(retryAfter, false);
}



  private activateRateLimitInternal(retryAfterSeconds: number, isHome: boolean): void {
    // Detener contador previo
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    // Inicializar el timestamp de la primera penalización
    this.lastPenaltyAddedAt = Date.now();

    // Activar estado
    this.rateLimitStateSubject.next({
      isActive: true,
      timeRemaining: retryAfterSeconds,
      isHome
    });

    // NO REDIRIGIR automáticamente - solo mostrar el overlay
    // La redirección automática genera nuevas peticiones y crea ciclos infinitos
    // El usuario puede navegar manualmente cuando termine el contador
    // console.log('[RateLimitService] 🚨 Bloqueo activado. Mostrando overlay SIN redirección automática.');
    this.startCountdown(retryAfterSeconds);
  }

  private startCountdown(initialSeconds: number) {
    // asegurar que el subject/interval previo se limpie
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    // fijar estado inicial redundante para asegurar consistencia
    this.rateLimitStateSubject.next({
      ...this.rateLimitStateSubject.value,
      isActive: true,
      timeRemaining: initialSeconds
    });

    this.countdownInterval = setInterval(() => {
      const state = this.rateLimitStateSubject.value;

      if (state.timeRemaining > 1) {
        this.rateLimitStateSubject.next({
          ...state,
          timeRemaining: state.timeRemaining - 1
        });
      } else {
        this.rateLimitStateSubject.next({
          isActive: false,
          timeRemaining: 0,
          isHome: state.isHome
        });

        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        // console.log('[RateLimitService] Rate limit finalizado. Recargando página actual...');

        // Recargar la página actual en lugar de redirigir al home
        setTimeout(() => window.location.reload(), 1000);

        // reset blockId tras finalizar el bloqueo para aceptar futuros bloqueos con mismo id si vuelve a ocurrir
        this.lastBlockId = null;
      }
    }, 1000);
  }

  // Métodos auxiliares públicos (tu API anterior)
  getCurrentState(): RateLimitState {
    return this.rateLimitStateSubject.value;
  }

  clear(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.rateLimitStateSubject.next({
      isActive: false,
      timeRemaining: 0,
      isHome: false
    });
    this.lastBlockId = null;
    this.lastActivationAt = 0;
    this.lastPenaltyAddedAt = 0; // Reiniciar timestamp de penalización
  }


/**
 * Marcar bloqueo PENDING inmediatamente para evitar que se envíen más peticiones.
 * No redirige ni inicia contador final — eso lo hará processGrouped429/activateRateLimitInternal.
 */
public markPendingBlock(retryAfterSeconds: number, blockId?: string | null, endpoint?: string): void {
  const limited = Math.min(Math.max(Math.floor(retryAfterSeconds || 0), 0), this.MAX_PENALTY_SECONDS);

  // Si ya hay bloqueo activo, SUMAR el nuevo tiempo SOLO si está dentro de la ventana de acumulación
  const current = this.rateLimitStateSubject.value;
  if (current.isActive) {
    const nowMs = Date.now();
    const timeSinceLastPenalty = nowMs - this.lastPenaltyAddedAt;

    // Solo sumar si está dentro de la ventana de acumulación
    if (timeSinceLastPenalty < this.ACCUMULATION_WINDOW_MS) {
      const newTimeRemaining = Math.min(
        current.timeRemaining + limited,
        this.MAX_PENALTY_SECONDS
      );

      // console.log('[RateLimitService] markPendingBlock: sumando', limited, 's al tiempo actual', current.timeRemaining, 's. Nuevo total:', newTimeRemaining, 's');

      this.rateLimitStateSubject.next({ ...current, timeRemaining: newTimeRemaining });
      this.lastPenaltyAddedAt = nowMs;
    } else {
      // console.log('[RateLimitService] markPendingBlock: fuera de ventana de acumulación. NO sumando.');
    }
    return;
  }

  // Establecer pending para bloquear nuevas solicitudes inmediatamente (sin redirigir)
  this.pendingBlock = true;
  this.pendingInfo = { retryAfterSeconds: limited, blockId: blockId ?? null, endpoint };

  // Actualizar state para que interceptores cancelen solicitudes futuras
  this.rateLimitStateSubject.next({
    isActive: true,
    timeRemaining: limited,
    isHome: false
  });

  // console.log('[RateLimitService] markPendingBlock: bloqueo PENDING activado inmediatamente (no-redir). retry=', limited, 'blockId=', blockId);
}




}
